<template>
  <b-sidebar
    id="sidebar-roadshow-score-review"
    :visible="sidebar.visible"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    backdrop
    no-header
    right
    @hidden="clearForm"
    @change="(val) => mutateSidebar({ visible: val })"
    @shown="onShow"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <div>
          <h4 class="mb-0">Revisar score</h4>
          <small class="text-muted">RoadShow</small>
        </div>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <div class="d-flex justify-content-center m-3" v-if="loading">
        <b-spinner variant="primary" />
      </div>

      <b-form v-else @submit.prevent="onSave" class="p-1">
        <p v-if="review.customer" class="mb-1">
          Candidato: <b>{{ review.customer.name }}</b>
        </p>

        <!-- Criteria list -->
        <b-list-group flush class="mb-1">
          <b-list-group-item
            v-for="criterion in criteria"
            :key="criterion.key"
            class="px-0"
            style=" background-color: transparent !important;"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div class="pr-1">
                <div class="font-weight-bold">{{ criterion.label }}</div>
                <small class="text-muted d-block">{{ criterion.focus }}</small>
                <small v-if="criterion.answer_label" class="d-block mt-25">
                  Resposta: <b>{{ criterion.answer_label }}</b>
                </small>
                <small
                  v-if="criterion.automated"
                  class="d-block text-muted mt-25"
                >
                  Automático: {{ formatScore(criterion.automatic_score) }} /
                  {{ formatScore(criterion.max) }}
                </small>
                <small v-else class="d-block text-muted mt-25">
                  Nota manual (feeling) — máx {{ formatScore(criterion.max) }}
                </small>
              </div>
              <b-form-input
                v-model.number="edited[criterion.key]"
                type="number"
                min="0"
                :max="criterion.max"
                step="0.5"
                class="score-input text-center"
                @input="onScoreInput(criterion)"
              />
            </div>
          </b-list-group-item>
        </b-list-group>

        <!-- Totals -->
        <b-card no-body class="border p-1 mb-1">
          <div class="d-flex justify-content-between">
            <span class="text-muted">Automático</span>
            <span>{{ formatScore(review.automatic_score) }}</span>
          </div>
          <div class="d-flex justify-content-between">
            <span class="text-muted">Ajuste manual</span>
            <span>{{ formatSigned(manualAdjustment) }}</span>
          </div>
          <hr class="my-1" />
          <div class="d-flex justify-content-between align-items-center">
            <span class="font-weight-bold">Total</span>
            <span class="font-weight-bold"
              >{{ formatScore(liveTotal) }} / {{ formatScore(maxTotal) }}</span
            >
          </div>
          <div
            v-if="review.classification"
            class="d-flex justify-content-between align-items-center mt-1"
          >
            <span class="text-muted">Classificação salva</span>
            <b-badge
              pill
              :variant="classificationVariant(review.classification)"
            >
              {{ review.classification }}
            </b-badge>
          </div>
        </b-card>

        <!-- Buttons -->
        <div class="d-flex justify-content-between mt-2">
          <b-button
            variant="outline-primary"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            @click="hide"
          >
            Voltar
          </b-button>
          <b-button
            type="submit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            :disabled="saving"
          >
            <b-spinner v-if="saving" small class="mr-50" />
            Salvar
          </b-button>
        </div>
      </b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BButton,
  BSpinner,
  BListGroup,
  BListGroupItem,
  BFormInput,
  BCard,
  BBadge,
} from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { mapGetters, mapActions, mapMutations } from "vuex";
import * as types from "../store/types";

export default {
  name: "RoadshowScoreReviewSidebar",
  components: {
    BSidebar,
    BForm,
    BButton,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BFormInput,
    BCard,
    BBadge,
  },
  directives: {
    Ripple,
  },
  props: {
    onSaved: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    return {
      loading: false,
      saving: false,
      review: {},
      criteria: [],
      edited: {},
    };
  },
  computed: {
    ...mapGetters({
      sidebar: types.ROADSHOW_SCORE_REVIEW_SIDEBAR,
    }),
    liveTotal() {
      return this.criteria.reduce((total, criterion) => {
        const value = Number(this.edited[criterion.key]);
        return total + (Number.isFinite(value) ? value : 0);
      }, 0);
    },
    maxTotal() {
      return this.criteria.reduce(
        (total, criterion) => total + Number(criterion.max || 0),
        0
      );
    },
    manualAdjustment() {
      return this.liveTotal - Number(this.review.automatic_score || 0);
    },
  },
  methods: {
    ...mapActions({
      getReview: types.GET_ROADSHOW_SCORE_REVIEW,
      saveReview: types.SAVE_ROADSHOW_SCORE_REVIEW,
    }),
    ...mapMutations({
      mutateSidebar: types.MUTATE_ROADSHOW_SCORE_REVIEW_SIDEBAR,
    }),
    async onShow() {
      if (!this.sidebar.customer_id) return;
      this.loading = true;
      try {
        const { data } = await this.getReview(this.sidebar.customer_id);
        this.review = data;
        this.criteria = (data.score_summary && data.score_summary.criteria) || [];
        this.edited = this.criteria.reduce((acc, criterion) => {
          acc[criterion.key] = Number(criterion.score || 0);
          return acc;
        }, {});
      } catch (err) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text:
              err?.response?.data?.message ||
              "Não foi possível carregar as pontuações do RoadShow.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        this.mutateSidebar({ visible: false });
      } finally {
        this.loading = false;
      }
    },
    onScoreInput(criterion) {
      const value = Number(this.edited[criterion.key]);
      const max = Number(criterion.max || 0);
      if (!Number.isFinite(value) || value < 0) {
        this.$set(this.edited, criterion.key, 0);
      } else if (value > max) {
        this.$set(this.edited, criterion.key, max);
      }
    },
    buildScores() {
      return this.criteria.map((criterion) => ({
        key: criterion.key,
        score: Number(this.edited[criterion.key]) || 0,
      }));
    },
    async onSave() {
      this.saving = true;
      try {
        const { data } = await this.saveReview({
          customerId: this.sidebar.customer_id,
          scores: this.buildScores(),
        });
        this.review = data;
        this.criteria =
          (data.score_summary && data.score_summary.criteria) || this.criteria;
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Sucesso",
            text: "Pontuação do RoadShow atualizada com sucesso.",
            icon: "CoffeeIcon",
            variant: "success",
          },
        });
        if (typeof this.sidebar.saveAction === "function") {
          this.sidebar.saveAction();
        }
        this.mutateSidebar({ visible: false });
      } catch (err) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text:
              err?.response?.data?.message ||
              "Não foi possível salvar a pontuação. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.saving = false;
      }
    },
    formatScore(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return "0";
      return Number.isInteger(number) ? String(number) : number.toFixed(1);
    },
    formatSigned(value) {
      const number = Number(value) || 0;
      const formatted = this.formatScore(Math.abs(number));
      if (number > 0) return `+${formatted}`;
      if (number < 0) return `-${formatted}`;
      return formatted;
    },
    classificationVariant(classification) {
      const normalized = (classification || "").toLowerCase();
      if (normalized.includes("alto")) return "success";
      if (normalized.includes("médio") || normalized.includes("medio"))
        return "warning";
      if (normalized.includes("reprove")) return "danger";
      return "secondary";
    },
    clearForm() {
      this.review = {};
      this.criteria = [];
      this.edited = {};
      this.mutateSidebar({ customer_id: undefined });
    },
  },
};
</script>

<style lang="scss" scoped>
.score-input {
  width: 64px;
  flex: 0 0 64px;
}
</style>
